import authService from './auth';

const API_URL = process.env.REACT_APP_API_URL || '';

/** If 401 with SESSION_REPLACED, clear token and redirect to login. */
async function handle401SessionReplaced(response) {
  if (response.status !== 401) return;
  try {
    const data = await response.clone().json();
    if (data?.code === 'SESSION_REPLACED') authService.clearTokenAndRedirectSessionReplaced();
  } catch (_) {}
}

// Simple API call function
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_URL}${endpoint}`;

  // Get auth token for protected endpoints
  const token = localStorage.getItem('novamatch_auth_token');
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    headers,
    ...options,
  });

  if (!response.ok) {
    await handle401SessionReplaced(response);
    const errorText = await response.text();
    throw new Error(`API call failed: ${response.status} ${response.statusText} - ${errorText}`);
  }

  return response.json();
};

// NovaMatch API functions
export const novaMatchAPI = {
  // Generic POST method for new endpoints
  post: async (endpoint, data = {}) => {
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      const error = new Error(errorData.error || `Request failed: ${response.status}`);
      error.response = { data: errorData, status: response.status };
      throw error;
    }
    return response.json();
  },

  analyze: async (resumeFile, jobDescription, mustHaveKeywords = [], { save = false, fastMode = false } = {}) => {
    const form = new FormData();
    form.append('resume', resumeFile);
    form.append('jobDescription', jobDescription);
    mustHaveKeywords.forEach(k => form.append('mustHaveKeywords[]', k));
    form.append('save', String(save));
    form.append('fastMode', String(fastMode));

    // Get auth token for protected endpoint
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch/analyze`, {
      method: 'POST',
      headers,
      body: form,
      // Don't set Content-Type for FormData - browser will handle it
    });

    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorText = await response.text();
      let errorData = null;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: errorText };
      }
      const error = new Error(errorData.error || `Failed to analyze resume: ${response.status} ${response.statusText}`);
      error.response = { data: errorData, status: response.status };
      error.code = errorData.code;
      error.validation = errorData.validation;
      throw error;
    }
    return response.json();
  },

  save: async ({ resumeFile, jobDescription, analysis, resumeFilename, resumeContentType }) => {
    const form = new FormData();
    form.append('resume', resumeFile);
    form.append('jobDescription', jobDescription);
    form.append('analysis', JSON.stringify(analysis));
    if (resumeFilename) form.append('resumeFilename', resumeFilename);
    if (resumeContentType) form.append('resumeContentType', resumeContentType);

    // Get auth token for protected endpoint
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch/save`, {
      method: 'POST',
      headers,
      body: form,
    });

    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorText = await response.text();
      throw new Error(`Failed to save analysis: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  },

  listScans: async ({ q = '', minScore = 0, hasAllMustHaves = false, page = 1, limit = 20 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    if (minScore) params.append('minScore', String(minScore));
    if (hasAllMustHaves) params.append('hasAllMustHaves', 'true');
    params.append('page', String(page));
    params.append('limit', String(limit));

    return apiCall(`/api/novamatch/scans?${params.toString()}`);
  },

  getScan: async (id) => {
    return apiCall(`/api/novamatch/scans/${id}`);
  },

  downloadFileUrl: (id) => `${API_URL}/api/novamatch/scans/${id}/file`,

  reanalyze: async (id) => {
    // Get auth token for protected endpoint
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch/reanalyze/${id}`, { 
      method: 'POST',
      headers
    });
    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorText = await response.text();
      throw new Error(`Failed to reanalyze: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  },

  delete: async (id) => {
    // Get auth token for protected endpoint
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch/scans/${id}`, { 
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorText = await response.text();
      throw new Error(`Failed to delete scan: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  },

  // V2 API functions (enhanced versions)
  analyzeV2: async (formData) => {
    // Get auth token for protected endpoint
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch/analyze`, {
      method: 'POST',
      headers,
      body: formData,
      // Don't set Content-Type for FormData - browser will handle it
    });

    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorText = await response.text();
      throw new Error(`Failed to analyze resume: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  },

  analyzeTextV2: async (resumeText, jobDescription, { fastMode = false } = {}) => {
    // Get auth token for protected endpoint
    const token = localStorage.getItem('novamatch_auth_token');
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/api/novamatch/analyze-text`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        resumeText,
        jobDescription,
        fastMode
      }),
    });

    if (!response.ok) {
      await handle401SessionReplaced(response);
      const errorText = await response.text();
      throw new Error(`Failed to analyze resume text: ${response.status} ${response.statusText} - ${errorText}`);
    }
    return response.json();
  },
};
